import React from 'react';

import DocumentActions from 'notebook/documents/DocumentActions';
import {WorksheetStepFrameProps} from 'notebook/worksheets/steps/WorksheetStepFrame';

import {
  StatusTypeToDisplayNameMapping,
  WORKSHEET_STEP_LOADING_STATUSES,
  WorksheetTaskStatusType,
} from '../WorksheetConstants';

const TERMINAL_STATES = [
  WorksheetTaskStatusType.COMPLETED,
  WorksheetTaskStatusType.SKIPPED,
  WorksheetTaskStatusType.FAILED,
];

export interface BannerMessageProps extends WorksheetStepFrameProps {
  isLatest?: boolean;
  latestVersionId?: number;
}

interface LatestVersionLinkProps {
  documentId: string;
  latestVersionId: number;
}

function LatestVersionLink({documentId, latestVersionId}: LatestVersionLinkProps): JSX.Element {
  const handleClick = (): void => {
    DocumentActions.loadVersion(documentId, latestVersionId);
  };
  return <a onClick={handleClick}>View the latest step version.</a>;
}

export default function BannerMessage(props: BannerMessageProps): JSX.Element | null {
  const {status} = props.stepTaskData;
  if (props.isWorksheetInReview) {
    return (
      <span>
        Editing is disabled because the worksheet is in review.{' '}
        {props.isLatest === false && props.latestVersionId && (
          <span>
            Currently viewing previous version.{' '}
            <LatestVersionLink documentId={props.document.id} latestVersionId={props.latestVersionId} />
          </span>
        )}
      </span>
    );
  }
  if (status && WORKSHEET_STEP_LOADING_STATUSES.includes(status)) {
    return <span>Editing is disabled because step is updating.</span>;
  } else if (props.isSelectedStepActionsLocked) {
    return (
      <span>
        Editing is disabled because the run has not started. <strong>Start run</strong> to unlock the Run
        steps.
      </span>
    );
  } else if (status && TERMINAL_STATES.includes(status)) {
    const statusText = StatusTypeToDisplayNameMapping[status].toLowerCase();
    const {reasonText, suffix} =
      props.isLatest === false && props.latestVersionId
        ? {
            reasonText: 'on previous versions. The step is',
            suffix: (
              <LatestVersionLink documentId={props.document.id} latestVersionId={props.latestVersionId} />
            ),
          }
        : {reasonText: 'because step has been', suffix: ''};
    return (
      <span>
        Editing is disabled {reasonText} <strong>{statusText}</strong>. {suffix}
      </span>
    );
  } else if (props.isLatest === false && props.latestVersionId) {
    return (
      <span>
        Editing is disabled on previous versions.{' '}
        <LatestVersionLink documentId={props.document.id} latestVersionId={props.latestVersionId} />
      </span>
    );
  } else if (props.stepTaskData.status === WorksheetTaskStatusType.NOT_READY) {
    return <span>Editing is disabled because the previous unit operation has not been completed.</span>;
  }

  return null;
}
